/**
 * Language Mismatch Modal - Zen & Minimal
 * Guides user to use the language selector instead of auto-switching
 */

import React from 'react'

export interface LanguageMismatchModalProps {
  detectedLanguage: string
  sourceLanguage: string
  targetLanguage: string
  confidence: number
  onKeep: () => void
  onCancel: () => void
}

const LanguageMismatchModal: React.FC<LanguageMismatchModalProps> = ({
  detectedLanguage,
  sourceLanguage,
  targetLanguage,
  onKeep,
  onCancel
}) => {
  // Get the full language names for display
  const getLanguageDisplay = (code: string) => {
    const lang = code.charAt(0).toUpperCase() + code.slice(1)
    return lang
  }

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 animate-in fade-in zoom-in duration-150">
        {/* Friendly message */}
        <p className="text-gray-800 text-center mb-4 text-base leading-relaxed">
          This looks like <span className="font-semibold text-gray-900">{getLanguageDisplay(detectedLanguage)}</span> code, but you selected <span className="font-semibold text-gray-900">{getLanguageDisplay(targetLanguage)}</span> to learn
        </p>

        {/* Helpful instruction */}
        <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mb-6">
          <p className="text-sm text-gray-700 text-center">
            Please paste <span className="font-medium text-gray-900">{getLanguageDisplay(targetLanguage)}</span> code — we'll explain it using your <span className="font-medium text-gray-900">{getLanguageDisplay(sourceLanguage)}</span> knowledge
          </p>
        </div>

        {/* Two action buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            data-testid="mismatch-button-cancel"
            aria-label="Dismiss language mismatch warning and paste correct code"
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md text-sm font-medium transition-colors"
          >
            OK, got it
          </button>
          <button
            onClick={onKeep}
            data-testid="mismatch-button-proceed"
            aria-label="Proceed with generation using selected language anyway"
            className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            Proceed Anyway
          </button>
        </div>
      </div>
    </div>
  )
}

export default LanguageMismatchModal
